import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  BaseExceptions,
  getMessageFromErrorResponse,
  isBaseError,
} from '@/api/v4/base-fetch';
import type { EditProjectPayload } from '@/api/v4/projects.api';
import { editProject } from '@/api/v4/projects.api';
import { QueryKey } from '@/config/query-client';
import { notify } from '@/ui/snackbar/notify';

import { projectIdState } from '../project.state';
import { useAtomValue } from 'jotai';

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  const projectId = useAtomValue(projectIdState);

  const mutation = useMutation({
    mutationFn: (data: EditProjectData) => {
      const { projectId, ...payload } = data;
      return editProject(projectId, payload);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKey.Projects] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.ProjectsKpi] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Project, projectId],
        }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.ProjectOptions] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.ProjectFiltersTeamOptions],
        }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.ProjectCreators] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.LastActivities] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.LastActivitiesCount],
        }),
      ]);
    },
    onError(error) {
      // Validation errors (e.g. a duplicate project name on rename) carry a
      // user-facing message. 500s are already toasted by the base-fetch
      // interceptor, so other errors are left to the caller.
      if (
        isBaseError(error) &&
        error.response.status === BaseExceptions.UNPROCESSABLE_ENTITY
      ) {
        const message = getMessageFromErrorResponse(error);
        if (message) {
          notify({ message });
        }
      }
    },
  });

  return {
    editProject: mutation.mutateAsync,
    isError: mutation.isError,
  };
};

interface EditProjectData extends EditProjectPayload {
  projectId: string;
}
